import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { fmtDate } from "@/lib/credits";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState } from "@/components/EmptyState";
import { Search, Loader2, BellRing, Phone, Mail } from "lucide-react";

export function FollowUpStatusBadge({ status, pastDue }) {
  const map = {
    pending: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    completed: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    skipped: "bg-white/5 text-neutral-400 border-white/10",
    unable_to_contact: "bg-[#E30613]/10 text-[#FF6069] border-[#E30613]/20",
    closed: "bg-white/5 text-neutral-400 border-white/10",
    needs_service: "bg-orange-500/10 text-orange-400 border-orange-500/20",
  };
  if (pastDue && status === "pending") {
    return <span className="inline-flex whitespace-nowrap rounded-full border border-[#E30613]/30 bg-[#E30613]/10 px-2.5 py-0.5 text-xs font-medium text-[#FF6069]" data-testid="fu-status-badge">Past Due</span>;
  }
  const label = { pending: "Pending", completed: "Completed", skipped: "Skipped", unable_to_contact: "Unable to Contact", closed: "Closed", needs_service: "Needs Service" }[status] || status;
  return <span className={`inline-flex whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-medium ${map[status] || "bg-white/5 text-neutral-400 border-white/10"}`} data-testid="fu-status-badge">{label}</span>;
}

export default function FollowUps() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("due");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { filter };
      if (isAdmin && q.trim()) params.q = q.trim();
      const { data } = await api.get("/follow-ups", { params });
      setRows(data);
    } catch (e) {} finally { setLoading(false); }
  }, [filter, q, isAdmin]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  return (
    <DashboardLayout title={isAdmin ? "Follow-Ups" : "Follow-Up Center"}>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#E30613]">Customer Care</p>
          <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight text-white">{isAdmin ? "Follow-up monitor" : "Customer follow-ups"}</h2>
          <p className="mt-1 text-sm text-neutral-500">{isAdmin ? "Track every 30-day customer follow-up across all installers." : "Reach out to your customers 30 days after installation."}</p>
        </div>
      </div>

      <div className="mb-5 flex flex-col gap-3 sm:flex-row">
        {isAdmin && (
          <div className="relative max-w-md flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search installer, customer, vehicle, VIN…" className="h-11 rounded-lg border-white/[0.1] bg-[#0A0A0A] pl-10 text-white placeholder:text-neutral-600 focus-visible:border-[#E30613] focus-visible:ring-1 focus-visible:ring-[#E30613]" data-testid="fu-search" />
          </div>
        )}
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="h-11 w-full rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white sm:w-52" data-testid="fu-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="border-white/[0.08] bg-[#141414] text-white">
            <SelectItem value="due">Due & Overdue</SelectItem>
            <SelectItem value="pending">All Pending</SelectItem>
            <SelectItem value="needs_service">Needs Service</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
            <SelectItem value="skipped">Skipped</SelectItem>
            <SelectItem value="unable_to_contact">Unable to Contact</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-hidden rounded-2xl border border-white/[0.06] bg-[#141414]" data-testid="follow-ups-table">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-neutral-500"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={BellRing}
            title="You're all caught up"
            description={isAdmin ? "Follow-ups will appear here as installers issue certificates." : "No follow-ups match this filter. New follow-ups are scheduled 30 days after each installation."}
            testid="followups-empty"
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-white/[0.06] text-xs uppercase tracking-wider text-neutral-500">
                  {isAdmin && <th className="px-6 py-4 font-medium">Installer</th>}
                  <th className="px-6 py-4 font-medium">Customer</th>
                  <th className="px-6 py-4 font-medium">Vehicle</th>
                  <th className="px-6 py-4 font-medium">Installed</th>
                  <th className="px-6 py-4 font-medium">Follow-Up</th>
                  <th className="px-6 py-4 font-medium">Overdue</th>
                  <th className="px-6 py-4 font-medium">Contact</th>
                  <th className="px-6 py-4 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} onClick={() => navigate(`/follow-ups/${r.id}`)} className="cursor-pointer border-b border-white/[0.04] transition-colors hover:bg-white/[0.03]" data-testid={`fu-row-${r.id}`}>
                    {isAdmin && <td className="px-6 py-4"><p className="text-sm text-neutral-300">{r.installer_business}</p></td>}
                    <td className="px-6 py-4"><p className="font-medium text-white">{r.customer_name}</p><p className="text-xs text-neutral-500">{r.certificate_number}</p></td>
                    <td className="px-6 py-4 text-sm text-neutral-300">{r.vehicle_year} {r.vehicle_make} {r.vehicle_model}</td>
                    <td className="px-6 py-4 text-sm text-neutral-400">{fmtDate(r.installation_date)}</td>
                    <td className="px-6 py-4 text-sm text-neutral-400">{fmtDate(r.follow_up_date)}</td>
                    <td className="px-6 py-4">
                      {r.follow_up_status === "pending" && r.days_overdue > 0
                        ? <span className="text-sm font-semibold text-[#FF6069]">{r.days_overdue}d</span>
                        : <span className="text-sm text-neutral-600">—</span>}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2 text-neutral-500">
                        {r.customer_phone && <Phone className="h-3.5 w-3.5" title={r.customer_phone} />}
                        {r.customer_email && <Mail className="h-3.5 w-3.5" title={r.customer_email} />}
                      </div>
                    </td>
                    <td className="px-6 py-4"><FollowUpStatusBadge status={r.follow_up_status} pastDue={r.past_due} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
